<template>
  <section>
    <v-form ref="form" v-model="valid" @submit.prevent="verify" lazy-validation>
      <v-card class="elevation-4">
        <v-toolbar color="grey darken-3" dark flat>
          <v-toolbar-title>VERIFY</v-toolbar-title>
          <v-spacer />
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <v-btn icon large :to="{ name: 'login' }" v-on="on">
                <v-icon>mdi-key</v-icon>
              </v-btn>
            </template>
            <span>Login</span>
          </v-tooltip>
        </v-toolbar>
        <v-progress-linear absolute indeterminate color="accent" height="7" v-if="loading"></v-progress-linear>
        <v-card-text>
          <v-alert border="left" colored-border type="error" v-if="error">
            <p>{{error}}</p>
            <p
              class="caption"
              v-if="notFound"
            >You've probably sent more than one verification email, please use the last one received.</p>
            <p
              class="caption"
              v-if="alreadyVerified"
            >This user is already verified, you can login or use the "forgot" link to reset your password.</p>
          </v-alert>
          <div v-if="token && !verifyExpired && !notFound">
            <p class="body-1" v-if="!verified">Please set a login password...</p>
            <p class="title text-center" v-if="verified">You've been verified! You can now login.</p>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  label="Password"
                  v-model="user.password"
                  prepend-icon="mdi-key"
                  type="password"
                  :rules="[(v) => !!v || 'Password is required']"
                  :error-messages="errors.password"
                  required
                  validate-on-blur
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  label="Confirm Password"
                  v-model="user.password_confirmation"
                  type="password"
                  :rules="passwordConfirmationRules"
                  :error-messages="errors.password_confirmation"
                  required
                  validate-on-blur
                />
              </v-col>
            </v-row>
          </div>
          <div v-if="signedUp || verifyResent">
            <p class="title">{{signedUp ? 'Thank you for signing up!' : 'Resent!'}}</p>
            <v-alert border="left" icon="mdi-email" colored-border color="accent" elevation="0">
              <p class="body-1">Please check your email for a verification email.</p>
              <p
                class="caption"
              >It can take a few minutes for the email to land in your inbox and be sure to also check your spam folder just in case.</p>
            </v-alert>
          </div>
          <div v-if="resendVerification || verifyExpired || notFound">
            <p
              class="body-1"
            >Please enter the email you signed up with. We'll resend the verification email.</p>
            <v-text-field
              label="Email"
              v-model="userEmail"
              name="email"
              prepend-icon="mdi-account"
              type="text"
              :rules="[(v) => !!v || 'Email is required']"
              required
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="ma-2" color outlined :to="{ name: 'reset' }" v-if="alreadyVerified">Forgot</v-btn>
          <v-btn
            class="ma-2"
            color="primary"
            type="submit"
            outlined
            :disabled="!valid"
            v-if="token && !verifyExpired && !notFound"
          >Verify</v-btn>
          <v-btn
            class="ma-2"
            color
            outlined
            @click="resend()"
            v-if="signedUp || resendVerification || verifyExpired || notFound"
          >Resend</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </section>
</template>

<script>
import { mapActions } from "vuex";
import { authManagement } from "@/api";

export default {
  data: () => ({
    loading: false,
    verified: false,
    verifyExpired: false,
    notFound: false,
    alreadyVerified: false,
    verifyResent: false,
    valid: false,
    email: null,
    user: {},
    errors: {},
    error: null,
  }),
  created() {},
  mounted() {},
  computed: {
    token() {
      return this.$route.query.token;
    },
    userEmail: {
      get() {
        return this.email;
      },
      set(email) {
        this.email = email.toLowerCase();
      },
    },
    signedUp() {
      return this.$route.query["signed-up"];
    },
    resendVerification() {
      return this.$route.query["resend"];
    },
    passwordConfirmationRules() {
      return [
        (v) => !!v || "Password confirmation is required",
        () =>
          this.user.password === this.user.password_confirmation ||
          "Password confirmation does not match password",
      ];
    },
  },
  methods: {
    ...mapActions("app", ["showMessage"]),
    validate() {
      return this.$refs.form.validate();
    },
    reset() {
      this.$refs.form.reset();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
      this.errors = {};
      this.error = null;
    },
    async resend() {
      if (!this.email && !this.$route.query.email) return;

      try {
        await authManagement.create({
          action: "resendVerifySignup",
          value: { email: this.$route.query.email || this.email },
        });
        this.showMessage({
          text: "Resending verification email!",
          color: "success",
        });
        this.verifyResent = true;
      } catch (error) {
        if (error.message.includes("already verified")) {
          this.alreadyVerified = true;
        }
        this.verifyResent = false;
        this.error = error.message;
      }
    },
    async verify() {
      if (!this.validate()) return;
      if (!this.token) return;

      this.loading = true;
      try {
        await authManagement.create({
          action: "verifySignupSetPasswordLong",
          value: {
            token: this.token, // compares to .verifyToken
            password: this.user.password, // new password
          },
        });
        this.verified = true;
        this.loading = false;

        this.showMessage({
          text: "Verified! You can now login...",
          color: "success",
        });
        this.$router.push({ name: "login" });
      } catch (error) {
        this.error = error.message;

        if (error.message.includes("not found")) {
          this.notFound = true;
        } else if (error.message.includes("already verified")) {
          this.alreadyVerified = true;
        } else {
          this.verifyExpired = true;
        }
        this.loading = false;
      }
    },
  },
  watch: {
    user: {
      handler() {
        if (!this.valid) {
          this.resetValidation();
        }
      },
    },
    email: {
      handler() {
        if (this.error) {
          this.resetValidation();
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
</style>